import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { piAdminService } from '../../services/piAdminService';
import { useAbortableFetch } from '../../hooks/useAbortableFetch';
import { mapSupabaseError } from '../../utils/supabaseErrors';

const OPS = ['>=', '>', '<=', '<', '=='];

// Parse a form string to a number, or null when blank.
const numOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};
// Parse a form string to a number, defaulting to 0 when blank/invalid.
const num0 = (v) => numOrNull(v) ?? 0;

// Self-contained Rewards builder for a PI System. Loads the system's enabled
// params + any existing formula on mount; Save persists via saveRewardFormula
// (atomic replace). Metric set = enabled param codes + derived net_deposit.
export function PISystemRewardsTab({ system }) {
  const [metrics, setMetrics] = useState([]); // [{ code, name }]
  const [payoutType, setPayoutType] = useState('linear');
  const [baseAmount, setBaseAmount] = useState('');
  const [capAmount, setCapAmount] = useState('');
  const [tierMetric, setTierMetric] = useState('');
  const [rates, setRates] = useState({}); // { [metricCode]: string }
  const [tiers, setTiers] = useState([]); // [{ min_value, max_value, amount }] (strings)
  const [gates, setGates] = useState([]); // [{ metric, op, threshold }] (threshold string)
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (ctx = {}) => {
    const isCancelled = ctx.isCancelled ?? (() => false);
    if (!system?.id) return;
    try {
      setLoading(true);
      setError('');
      const [enabled, reward] = await Promise.all([
        piAdminService.getSystemParameters(system.id),
        piAdminService.getRewardFormula(system.id),
      ]);
      if (isCancelled()) return;

      const metricList = [
        ...enabled.map((row) => ({
          code: row.pi_parameters?.code,
          name: row.pi_parameters?.name || row.pi_parameters?.code,
        })),
        { code: 'net_deposit', name: 'Net Deposit' },
      ].filter((m) => m.code);
      setMetrics(metricList);

      const f = reward.formula;
      if (f) {
        setPayoutType(f.payout_type || 'linear');
        setBaseAmount(f.base_amount != null ? String(f.base_amount) : '');
        setCapAmount(f.cap_amount != null ? String(f.cap_amount) : '');
        setTierMetric(f.tier_metric || '');
      }
      const rateMap = {};
      (reward.rates || []).forEach((r) => { rateMap[r.metric] = String(r.rate); });
      setRates(rateMap);
      setTiers((reward.tiers || []).map((t) => ({
        min_value: t.min_value != null ? String(t.min_value) : '',
        max_value: t.max_value != null ? String(t.max_value) : '',
        amount: t.amount != null ? String(t.amount) : '',
      })));
      setGates((reward.gates || []).map((g) => ({
        metric: g.metric,
        op: g.op,
        threshold: g.threshold != null ? String(g.threshold) : '',
      })));
    } catch (err) {
      if (isCancelled()) return;
      setError(mapSupabaseError(err));
    } finally {
      if (!isCancelled()) {
        setLoading(false);
        setReady(true);
      }
    }
  }, [system?.id]);

  useAbortableFetch(({ isCancelled }) => { load({ isCancelled }); }, [system?.id, load]);

  const setRate = (code, value) => setRates((prev) => ({ ...prev, [code]: value }));

  const addTier = () => setTiers((prev) => [...prev, { min_value: '', max_value: '', amount: '' }]);
  const removeTier = (index) => setTiers((prev) => prev.filter((_, i) => i !== index));
  const setTierField = (index, field, value) =>
    setTiers((prev) => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)));

  const addGate = () => setGates((prev) => [...prev, { metric: metrics[0]?.code || 'deposits', op: '>=', threshold: '' }]);
  const removeGate = (index) => setGates((prev) => prev.filter((_, i) => i !== index));
  const setGateField = (index, field, value) =>
    setGates((prev) => prev.map((g, i) => (i === index ? { ...g, [field]: value } : g)));

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      const payload = {
        payout_type: payoutType,
        base_amount: num0(baseAmount),
        cap_amount: numOrNull(capAmount),
        tier_metric: payoutType === 'tiered' ? (tierMetric || null) : null,
        gates: gates
          .filter((g) => g.metric && g.op && g.threshold !== '')
          .map((g) => ({ metric: g.metric, op: g.op, threshold: num0(g.threshold) })),
        rates: payoutType === 'linear'
          ? metrics
              .filter((m) => rates[m.code] !== undefined && rates[m.code] !== '' && num0(rates[m.code]) !== 0)
              .map((m) => ({ metric: m.code, rate: num0(rates[m.code]) }))
          : [],
        tiers: payoutType === 'tiered'
          ? tiers
              .filter((t) => t.min_value !== '' || t.amount !== '')
              .map((t) => ({ min_value: num0(t.min_value), max_value: numOrNull(t.max_value), amount: num0(t.amount) }))
          : [],
      };
      await piAdminService.saveRewardFormula(system.id, payload);
      toast.success('Reward formula saved');
    } catch (err) {
      const mapped = mapSupabaseError(err);
      setError(mapped);
      toast.error(mapped);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pi-rewards-tab">
      {error && <div className="error-message" role="alert">{error}</div>}
      {!ready && !error && <div className="loading-indicator">Loading…</div>}
      {ready && (
      <div className="reward-form">

      <div className="form-group">
        <label htmlFor="reward-payout-type">Payout type</label>
        <select
          id="reward-payout-type"
          value={payoutType}
          onChange={(e) => setPayoutType(e.target.value)}
          disabled={loading}
        >
          <option value="linear">Linear</option>
          <option value="tiered">Tiered</option>
        </select>
      </div>

      {payoutType === 'linear' && (
        <div className="reward-linear">
          <div className="form-group">
            <label htmlFor="reward-base-amount">Base amount</label>
            <input
              id="reward-base-amount"
              type="number"
              step="0.01"
              value={baseAmount}
              onChange={(e) => setBaseAmount(e.target.value)}
              disabled={loading}
            />
          </div>
          <h4>Rates (per metric)</h4>
          {metrics.map((m) => (
            <div key={m.code} className="form-group">
              <label htmlFor={`reward-rate-${m.code}`}>Rate for {m.code}</label>
              <input
                id={`reward-rate-${m.code}`}
                type="number"
                step="0.0001"
                value={rates[m.code] ?? ''}
                onChange={(e) => setRate(m.code, e.target.value)}
                disabled={loading}
              />
            </div>
          ))}
        </div>
      )}

      {payoutType === 'tiered' && (
        <div className="reward-tiered">
          <div className="form-group">
            <label htmlFor="reward-tier-metric">Tier metric</label>
            <select
              id="reward-tier-metric"
              value={tierMetric}
              onChange={(e) => setTierMetric(e.target.value)}
              disabled={loading}
            >
              <option value="">Select metric…</option>
              {metrics.map((m) => (
                <option key={m.code} value={m.code}>{m.code}</option>
              ))}
            </select>
          </div>
          <table className="reward-tiers-table">
            <thead>
              <tr><th>Min</th><th>Max</th><th>Amount</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {tiers.map((t, index) => (
                <tr key={index}>
                  <td>
                    <input
                      type="number" step="0.01"
                      aria-label={`Tier ${index + 1} min`}
                      value={t.min_value}
                      onChange={(e) => setTierField(index, 'min_value', e.target.value)}
                      disabled={loading}
                    />
                  </td>
                  <td>
                    <input
                      type="number" step="0.01"
                      aria-label={`Tier ${index + 1} max`}
                      value={t.max_value}
                      onChange={(e) => setTierField(index, 'max_value', e.target.value)}
                      disabled={loading}
                    />
                  </td>
                  <td>
                    <input
                      type="number" step="0.01"
                      aria-label={`Tier ${index + 1} amount`}
                      value={t.amount}
                      onChange={(e) => setTierField(index, 'amount', e.target.value)}
                      disabled={loading}
                    />
                  </td>
                  <td>
                    <button type="button" className="btn-secondary" onClick={() => removeTier(index)} disabled={loading}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="btn-secondary" onClick={addTier} disabled={loading}>
            + Add tier
          </button>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="reward-cap-amount">Cap amount</label>
        <input
          id="reward-cap-amount"
          type="number"
          step="0.01"
          value={capAmount}
          onChange={(e) => setCapAmount(e.target.value)}
          disabled={loading}
        />
      </div>

      <div className="reward-gates">
        <h4>Gate conditions (optional — all must pass)</h4>
        <table className="reward-gates-table">
          <thead>
            <tr><th>Metric</th><th>Operator</th><th>Threshold</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {gates.map((g, index) => (
              <tr key={index}>
                <td>
                  <select
                    aria-label={`Gate ${index + 1} metric`}
                    value={g.metric}
                    onChange={(e) => setGateField(index, 'metric', e.target.value)}
                    disabled={loading}
                  >
                    {metrics.map((m) => (
                      <option key={m.code} value={m.code}>{m.code}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    aria-label={`Gate ${index + 1} operator`}
                    value={g.op}
                    onChange={(e) => setGateField(index, 'op', e.target.value)}
                    disabled={loading}
                  >
                    {OPS.map((op) => (
                      <option key={op} value={op}>{op}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="number" step="0.01"
                    aria-label={`Gate ${index + 1} threshold`}
                    value={g.threshold}
                    onChange={(e) => setGateField(index, 'threshold', e.target.value)}
                    disabled={loading}
                  />
                </td>
                <td>
                  <button type="button" className="btn-secondary" onClick={() => removeGate(index)} disabled={loading}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" className="btn-secondary" onClick={addGate} disabled={loading}>
          + Add gate condition
        </button>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
        <button type="button" className="btn-primary" onClick={handleSave} disabled={loading}>
          Save Reward Formula
        </button>
      </div>

      </div>
      )}
    </div>
  );
}
